import { Component, inject, input, output, signal, computed, LOCALE_ID, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { finalize } from 'rxjs';
import { FilterProductSearch } from '../../../../../shared/ui/shared-filters/filter-product-search/filter-product-search';
import { FilterDateComponent } from '../../../../../shared/ui/shared-filters/filter-date-component/filter-date-component';
import { FilterSortingComponent } from '../../../../../shared/ui/shared-filters/filter-sorting-component/filter-sorting-component';
import { FilterInput } from '../../../../../shared/ui/shared-filters/filter-input/filter-input';
import { CartService } from '../../../../../services/cart/cart';
import { PharmacyService } from '../../../../../services/pharmacy/pharmacy.service';
import { ApprovedPharmaciesService } from '../../../../../services/pharmacy/approved-pharmacies.service';
import { CartFiltersApi } from '../../../../../pages/admin-dashboard/cart-management/admin-cart.model';
import { SCOPES } from '../../../../../services/scope/scope-constants';
import { HasScopeDirective } from '../../../../../services/scope/has-scope.directive';
import { SubmitButton } from '../../../../../shared/ui/Buttons/submit-button/submit-button';

@Component({
  selector: 'app-cart-list-filters',
  standalone: true,
  imports: [
    FormsModule,
    HasScopeDirective,
    FilterProductSearch,
    FilterDateComponent,
    FilterSortingComponent,
    FilterInput,
    SubmitButton
],
  templateUrl: './cart-list-filters.html',
  styleUrl: './cart-list-filters.scss',
})
export class CartListFiltersComponent implements OnInit {
  readonly SCOPES = SCOPES;

  private locale = inject(LOCALE_ID);
  private cartService = inject(CartService);
  private pharmacyService = inject(PharmacyService);
  private approvedPharmaciesService = inject(ApprovedPharmaciesService);

  filters = input.required<CartFiltersApi>();

  applyFilters = output<CartFiltersApi>();
  resetFilters = output();
  toggleSortOrder = output();

  isArabic = computed(() => {
    console.log('locale: ', this.locale);
    return this.locale.startsWith('ar');
  });

  cartTitleSearch = signal('');

  pharmacySearchTerm = signal('');
  pharmacyResults = signal<{ id: number; titleAr: string; titleEn: string }[]>([]);
  pharmacyLoading = signal(false);
  pharmacyError = signal('');
  showPharmacyDropdown = signal(false);
  selectedPharmacyId = signal<number | null>(null);

  ownerSearchTerm = signal('');
  ownerResults = signal<{ id: number; titleAr: string; titleEn: string }[]>([]);
  ownerLoading = signal(false);
  ownerError = signal('');
  showOwnerDropdown = signal(false);
  selectedOwnerId = signal<number | null>(null);

  assignedSearchTerm = signal('');
  assignedResults = signal<{ id: number; titleAr: string; titleEn: string }[]>([]);
  assignedLoading = signal(false);
  assignedError = signal('');
  showAssignedDropdown = signal(false);
  selectedAssignedIds = signal<{ id: number; fullName: string }[]>([]);

  createdOnFrom = signal<string | null>(null);
  createdOnTo = signal<string | null>(null);
  lastModifiedOnFrom = signal<string | null>(null);
  lastModifiedOnTo = signal<string | null>(null);

  createdDateInvalid = signal(false);
  lastModifiedDateInvalid = signal(false);

  localOrderBy = signal('CREATION_DATE');

  sortOptions = computed(() => [
    { value: 'CREATION_DATE', labelEn: 'Creation Date', labelAr: 'تاريخ الإنشاء' },
    { value: 'LAST_MODIFICATION_DATE', labelEn: 'Last Modified', labelAr: 'آخر تعديل' },
  ]);

  ngOnInit(): void {
    if (this.filters()?.orderBy) {
      this.localOrderBy.set(this.filters().orderBy!);
    }
  }

  onPharmacySearchChange(value: string) {
    this.pharmacySearchTerm.set(value);
    if (!value) {
      this.showPharmacyDropdown.set(false);
      this.pharmacyResults.set([]);
      this.selectedPharmacyId.set(null);
      return;
    }
    if (value.length < 2) return;
    this.pharmacyLoading.set(true);
    this.showPharmacyDropdown.set(true);
    this.cartService
      .getPharmacyAutoComplete({ quickSearch: value })
      .pipe(finalize(() => this.pharmacyLoading.set(false)))
      .subscribe({
        next: (res) => {
          if (res && res.list) {
            this.pharmacyResults.set(
              res.list
                .filter((i) => i)
                .map((i) => ({ id: i.id, titleAr: i.nickName || '', titleEn: i.nickName || '' })),
            );
          }
        },
        error: () => this.pharmacyError.set(this.isArabic() ? 'حدث خطأ' : 'Error fetching results'),
      });
  }

  onPharmacySelect(item: { id: number; titleAr: string; titleEn: string }) {
    this.selectedPharmacyId.set(item.id);
    this.pharmacySearchTerm.set(this.isArabic() ? item.titleAr : item.titleEn);
    this.showPharmacyDropdown.set(false);
  }

  onOwnerSearchChange(value: string) {
    this.ownerSearchTerm.set(value);
    if (!value) {
      this.showOwnerDropdown.set(false);
      this.ownerResults.set([]);
      this.selectedOwnerId.set(null);
      return;
    }
    if (value.length < 2) return;
    this.ownerLoading.set(true);
    this.showOwnerDropdown.set(true);
    this.pharmacyService
      .pharmacyOwnerAutoComplete(value)
      .pipe(finalize(() => this.ownerLoading.set(false)))
      .subscribe({
        next: (res) => {
          if (res && res.list) {
            this.ownerResults.set(
              (res.list as any[])
                .filter((i) => i)
                .map((i) => ({ id: i.id, titleAr: i.fullName || '', titleEn: i.fullName || '' })),
            );
          }
        },
        error: () => this.ownerError.set(this.isArabic() ? 'حدث خطأ' : 'Error fetching results'),
      });
  }

  onOwnerSelect(item: { id: number; titleAr: string; titleEn: string }) {
    this.selectedOwnerId.set(item.id);
    this.ownerSearchTerm.set(item.titleEn);
    this.showOwnerDropdown.set(false);
  }

  onAssignedSearchChange(value: string) {
    this.assignedSearchTerm.set(value);
    if (!value) {
      this.showAssignedDropdown.set(false);
      this.assignedResults.set([]);
      return;
    }
    if (value.length < 2) return;
    this.assignedLoading.set(true);
    this.showAssignedDropdown.set(true);
    this.approvedPharmaciesService
      .teleSalesEmployeeAutoComplete(value)
      .pipe(finalize(() => this.assignedLoading.set(false)))
      .subscribe({
        next: (res) => {
          if (res && res.list) {
            this.assignedResults.set(
              (res.list as any[])
                .filter((i) => i)
                .map((i) => ({ id: i.id, titleAr: i.fullName || '', titleEn: i.fullName || '' })),
            );
          }
        },
        error: () => this.assignedError.set(this.isArabic() ? 'حدث خطأ' : 'Error fetching results'),
      });
  }

  onAssignedSelect(item: { id: number; titleAr: string; titleEn: string }) {
    const current = this.selectedAssignedIds();
    if (!current.find((x) => x.id === item.id)) {
      this.selectedAssignedIds.set([...current, { id: item.id, fullName: item.titleEn }]);
    }
    this.assignedSearchTerm.set('');
    this.showAssignedDropdown.set(false);
    this.assignedResults.set([]);
  }

  removeAssignedTag(id: number) {
    this.selectedAssignedIds.update((list) => list.filter((x) => x.id !== id));
  }

  onToggleSortOrder() {
    this.toggleSortOrder.emit();
  }

  onSortByChange(newOrder: string) {
    this.localOrderBy.set(newOrder);
  }

  onContainerClick(event: Event) {}

  onResetFilters() {
    this.cartTitleSearch.set('');
    this.pharmacySearchTerm.set('');
    this.selectedPharmacyId.set(null);
    this.ownerSearchTerm.set('');
    this.selectedOwnerId.set(null);
    this.assignedSearchTerm.set('');
    this.selectedAssignedIds.set([]);
    this.createdOnFrom.set(null);
    this.createdOnTo.set(null);
    this.lastModifiedOnFrom.set(null);
    this.lastModifiedOnTo.set(null);
    this.createdDateInvalid.set(false);
    this.lastModifiedDateInvalid.set(false);
    this.pharmacyResults.set([]);
    this.ownerResults.set([]);
    this.assignedResults.set([]);
    this.showPharmacyDropdown.set(false);
    this.showOwnerDropdown.set(false);
    this.showAssignedDropdown.set(false);
    this.localOrderBy.set('CREATION_DATE');
    this.resetFilters.emit();
  }

  onApplyFilters() {
    const createdInvalid = !!(
      this.createdOnFrom() &&
      this.createdOnTo() &&
      this.createdOnFrom()! > this.createdOnTo()!
    );
    const lastModInvalid = !!(
      this.lastModifiedOnFrom() &&
      this.lastModifiedOnTo() &&
      this.lastModifiedOnFrom()! > this.lastModifiedOnTo()!
    );
    this.createdDateInvalid.set(createdInvalid);
    this.lastModifiedDateInvalid.set(lastModInvalid);
    if (createdInvalid || lastModInvalid) return;

    this.applyFilters.emit({
      cartTitle: this.cartTitleSearch() || undefined,
      pharmacyId: this.selectedPharmacyId() ?? undefined,
      pharmacyOwnerId: this.selectedOwnerId() ?? undefined,
      assignedToIds: this.selectedAssignedIds().length ? this.selectedAssignedIds().map((x) => x.id) : undefined,
      createdOnFrom: this.createdOnFrom() ?? undefined,
      createdOnTo: this.createdOnTo() ?? undefined,
      lastModifiedOnFrom: this.lastModifiedOnFrom() ?? undefined,
      lastModifiedOnTo: this.lastModifiedOnTo() ?? undefined,
      orderBy: this.localOrderBy(),
    });
  }
}
